import {
  CreateAdFillIcon,
  CreateAdOutlineIcon,
  MeinsFillIcon,
  MeinsOutlineIcon,
} from "@kds-web/icons";
import clsx from "clsx";
import { useState, useRef } from "react";
import type React from "react";
import type { JSX } from "react/jsx-runtime";
import { NAV_SUB_MENU_LOWER } from "../../constants";
import type { MyMenuOptions } from "./myMenuOptions";
import { getNavSubUpperOptions } from "./navSubMenuUpperOptions";

type Props = {
  postAdHighlighted: boolean;
  myAdsHighlighted: boolean;
  myMenuOptions: MyMenuOptions;
};

const SUB_MENU_ID = "nav-sub-menu";

function NavMenu({
  postAdHighlighted,
  myAdsHighlighted,
  myMenuOptions,
}: Props): JSX.Element {
  const NAV_MENU_ITEMS = [
    {
      href: "/p-anzeige-aufgeben-schritt2.html",
      label: "Inserieren",
      icon: postAdHighlighted ? <CreateAdFillIcon /> : <CreateAdOutlineIcon />,
      highlighted: postAdHighlighted,
      id: "nav-menu-item-post-ad",
    },
    {
      href: "/m-meine-anzeigen.html",
      label: "Meins",
      icon: myAdsHighlighted ? <MeinsFillIcon /> : <MeinsOutlineIcon />,
      submenu: <NavSubMenu myMenuOptions={myMenuOptions} />,
      highlighted: myAdsHighlighted,
      id: "nav-menu-item-my-ads",
    },
  ];

  return (
    <nav aria-label="Hauptnavigation">
      <ul className="flex h-full min-h-[80px] rounded-small">
        {NAV_MENU_ITEMS.map((item) => (
          <NavMenuItem key={item.id} {...item} />
        ))}
      </ul>
    </nav>
  );
}

function NavMenuItem({
  href,
  label,
  icon,
  submenu,
  highlighted,
  id,
}: {
  href: string;
  label: string;
  icon: JSX.Element;
  submenu?: JSX.Element;
  highlighted: boolean;
  id: string;
}): JSX.Element {
  const [isSubMenuVisible, setSubMenuVisible] = useState(false);
  const menuItemRef = useRef<HTMLLIElement>(null);

  function openSubMenu(): void {
    if (submenu) {
      setSubMenuVisible(true);
    }
  }

  function closeSubMenuOnBlur(
    e: FocusEvent | React.FocusEvent<HTMLLIElement>,
  ): void {
    const relatedTarget =
      "relatedTarget" in e
        ? (e as FocusEvent).relatedTarget
        : (e as React.FocusEvent<HTMLLIElement>).relatedTarget;
    const focusOutsideMenuItem = !menuItemRef.current?.contains(
      relatedTarget as Node,
    );
    if (submenu && focusOutsideMenuItem) {
      setSubMenuVisible(false);
    }
  }

  function closeSubMenuOnMouseLeave(): void {
    if (submenu) {
      setSubMenuVisible(false);
    }
  }

  return (
    <li
      className="relative mt-xxsmall flex text-onPrimary"
      ref={menuItemRef}
      onFocus={openSubMenu}
      onBlur={closeSubMenuOnBlur}
      onMouseEnter={openSubMenu}
      onMouseLeave={closeSubMenuOnMouseLeave}
    >
      {/* TODO: add tracking (see Belen) */}
      <a
        id={id}
        className={clsx(
          "flex flex-col items-center justify-center px-small",
          "border-b-4 border-transparent focus-within:border-interactive hover:border-interactive",
          highlighted && "!border-interactive",
          isSubMenuVisible && "!border-interactive",
        )}
        href={href}
        aria-expanded={isSubMenuVisible}
      >
        <span className="size-large">{icon}</span>
        <span className="text-bodyRegularStrong">{label}</span>
      </a>
      {isSubMenuVisible && submenu}
    </li>
  );
}

function NavSubMenu({
  myMenuOptions,
}: {
  myMenuOptions: MyMenuOptions;
}): JSX.Element {
  const navSubUpperOptions = getNavSubUpperOptions(myMenuOptions);
  return (
    <ul
      id={SUB_MENU_ID}
      className="absolute right-xxxsmall top-full z-dropdown list-none rounded-b-small border border-t-0 border-utilityNonessential bg-surface p-xsmall pb-none"
    >
      {navSubUpperOptions.map((item) => (
        <li key={item.label} className="p-xsmall pt-none">
          <a className="hover:underline" href={item.href}>
            {item.label}
          </a>
        </li>
      ))}
      <hr className="box-content border-t border-utilityNonessential" />
      <div className="pb-xsmall pr-small pt-small text-bodySmall text-onSurfaceNonessential">
        FAVORITEN
      </div>
      {NAV_SUB_MENU_LOWER.map((item) => (
        <li key={item.label} className="p-xsmall pt-none">
          <a className="hover:underline" href={item.href}>
            {item.label}
          </a>
        </li>
      ))}
    </ul>
  );
}

export default NavMenu;
